/**
 * EventPassViewer — the passes a checkout produced, one at a time.
 *
 * A person who enters the 21K twice and the yoga once leaves with THREE passes,
 * each its own QR and its own code, because each is its own registration. They
 * are shown one at a time with a way to step between them: three QR codes side
 * by side are three things to scan by mistake, and at a gate that matters.
 *
 * THE PICTURE IS NEVER INJECTED AS MARKUP. The QR arrives as SVG text from the
 * server and is put through the platform's own `parseQrSvg`, which extracts the
 * viewBox and the path data and rebuilds the element from those — so nothing
 * the server sent becomes markup in this document. That parser is shared with
 * PublicSuccessModal deliberately: two copies of an XSS boundary is one copy
 * too many.
 */

import React, { useEffect, useRef, useState } from 'react';
import { parseQrSvg } from '../../public/PublicSuccessModal';

/**
 * The pass's own label/value pairs, exactly as the button's reveal configured
 * them — "Pass code", a reference, whatever this application issues. They are
 * NOT read by a fixed key: the server returns `fields`, and which fields those
 * are is a matter of configuration, so hunting for `pass_code` in it finds
 * nothing on an application that calls it something else.
 */
function firstValue(pass) {
  const field = (pass.fields || []).find(f => f && f.value);
  return field ? field.value : '';
}

/** One pass as a standalone SVG file, ready to be saved. */
function svgFileFor(pass) {
  const qr = parseQrSvg(pass.qrSvg);
  if (!qr) return null;

  const code = firstValue(pass);
  // STROKED, not filled. segno draws a QR as horizontal runs — `M2 2.5h7…` —
  // which enclose no area at all, so a fill paints an empty square.
  const paths = qr.ds
    .map(d => `<path d="${d}" fill="none" stroke="#101816" stroke-width="1"/>`)
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="${qr.viewBox}" width="512" height="512" role="img" aria-label="Entry pass ${code}">
<title>${pass.activityName || 'Entry pass'}${code ? ` — ${code}` : ''}</title>
<rect width="100%" height="100%" fill="#ffffff"/>
${paths}
</svg>`;
}

/**
 * EVERY pass on ONE sheet.
 *
 * Not one file per pass. A browser permits a single download per gesture and
 * silently drops the rest, so ten passes arrived as one file and nine
 * disappeared with no error anywhere — the visitor believes they have them all
 * until they reach the gate. One file cannot be half-delivered.
 *
 * It is also the more useful object: a sheet of ten codes is what somebody
 * prints and hands round, where ten separate files are ten things to find.
 */
function sheetFor(passes, eventTitle) {
  const cells = passes
    .map(pass => ({ pass, qr: parseQrSvg(pass.qrSvg) }))
    .filter(cell => cell.qr);

  if (cells.length === 0) return null;

  const COLUMNS = 2;
  const CELL_W = 300;
  const CELL_H = 350;
  const QR_SIZE = 230;
  const MARGIN = 40;
  const HEADER = 70;

  const rows = Math.ceil(cells.length / COLUMNS);
  const width = MARGIN * 2 + CELL_W * COLUMNS;
  const height = MARGIN * 2 + HEADER + CELL_H * rows;

  const escape = text => String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const drawn = cells.map(({ pass, qr }, index) => {
    const column = index % COLUMNS;
    const row = Math.floor(index / COLUMNS);
    const x = MARGIN + column * CELL_W;
    const y = MARGIN + HEADER + row * CELL_H;

    // Each QR has its own viewBox — 33 units, 37, whatever the payload needed —
    // so each is scaled to the same drawn size rather than assumed equal.
    const [, , vbW] = qr.viewBox.trim().split(/\s+/).map(Number);
    const scale = QR_SIZE / (vbW || 1);
    const qrX = x + (CELL_W - QR_SIZE) / 2;

    const paths = qr.ds
      .map(d => `<path d="${d}" fill="none" stroke="#101816" stroke-width="1"/>`)
      .join('');

    return `  <g>
    <rect x="${x + 10}" y="${y}" width="${CELL_W - 20}" height="${CELL_H - 20}" rx="12" fill="#ffffff" stroke="#dbe3df"/>
    <g transform="translate(${qrX}, ${y + 20}) scale(${scale})">${paths}</g>
    <text x="${x + CELL_W / 2}" y="${y + QR_SIZE + 58}" text-anchor="middle" font-family="Inter, Helvetica, Arial, sans-serif" font-size="15" font-weight="600" fill="#101816">${escape(pass.activityName)}</text>
    <text x="${x + CELL_W / 2}" y="${y + QR_SIZE + 84}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="17" letter-spacing="2" fill="#b4531b">${escape(firstValue(pass))}</text>
  </g>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Entry passes for ${escape(eventTitle)}">
<title>${escape(eventTitle)} — ${cells.length} entry passes</title>
<rect width="100%" height="100%" fill="#f3f7f5"/>
<text x="${MARGIN}" y="${MARGIN + 20}" font-family="Poppins, Inter, Helvetica, sans-serif" font-size="22" font-weight="600" fill="#101816">${escape(eventTitle)}</text>
<text x="${MARGIN}" y="${MARGIN + 44}" font-family="Inter, Helvetica, Arial, sans-serif" font-size="13" fill="#69786f">${cells.length} entry passes · show one per person at the gate</text>
${drawn}
</svg>`;
}

/** Saves one file without leaving a dangling object URL behind. */
function saveFile(name, text) {
  const blob = new Blob([text], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download in
  // some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slug(text) {
  return String(text || 'pass').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function EventPassViewer({ passes, eventTitle }) {
  const [index, setIndex] = useState(0);
  const [saved, setSaved] = useState(null);
  const liveRef = useRef(null);

  const total = Array.isArray(passes) ? passes.length : 0;

  // A pass list that shrinks (a re-run of the same checkout) must not leave the
  // viewer pointing past the end.
  useEffect(() => {
    if (index > total - 1) setIndex(Math.max(0, total - 1));
  }, [total, index]);

  if (total === 0) return null;

  const pass = passes[Math.min(index, total - 1)];
  const qr = parseQrSvg(pass.qrSvg);

  function downloadAll() {
    // ONE file, always. A browser allows one download per gesture and drops the
    // rest without a word, so "download all" used to deliver the first pass and
    // silently lose the other nine.
    const single = total === 1 ? svgFileFor(pass) : null;
    const text = single || sheetFor(passes, eventTitle);
    if (!text) {
      setSaved('There is nothing to download for these passes.');
      return;
    }

    const name = single
      ? `${slug(eventTitle)}-${slug(pass.activityName)}-${firstValue(pass) || 'pass'}.svg`
      : `${slug(eventTitle)}-${total}-passes.svg`;

    saveFile(name, text);
    setSaved(single ? 'Pass downloaded.' : `All ${total} passes downloaded on one sheet.`);
  }

  return (
    <div className="evb-passes">
      <div className="evb-passes__head">
        <span className="evb-passes__k">
          {total === 1 ? 'Your entry pass' : `Pass ${index + 1} of ${total}`}
        </span>
        {total > 1 && (
          <div className="evb-passes__nav">
            <button
              type="button"
              className="evb-passes__step"
              onClick={() => setIndex(i => Math.max(0, i - 1))}
              disabled={index === 0}
              aria-label="Previous pass"
            >
              ‹
            </button>
            <button
              type="button"
              className="evb-passes__step"
              onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
              disabled={index === total - 1}
              aria-label="Next pass"
            >
              ›
            </button>
          </div>
        )}
      </div>

      <p className="evb-passes__for">{pass.activityName}</p>

      {qr ? (
        <div className="evb-passes__qr">
          <svg viewBox={qr.viewBox} width="100%" height="100%" role="img"
               aria-label={`Entry pass for ${pass.activityName}`}>
            {qr.ds.map((d, i) => (
              <path key={i} d={d} fill="none" stroke="currentColor" strokeWidth="1" />
            ))}
          </svg>
        </div>
      ) : (
        // No picture is not an error: the code below is the pass, and a gate can
        // take it typed. Saying so beats an empty frame.
        <p className="evb-passes__noqr">Show the code below at the gate.</p>
      )}

      {(pass.fields || []).filter(f => f && f.value).map(field => (
        <div className="evb-passes__code" key={field.label}>
          <span>{field.label}</span>
          <b>{field.value}</b>
        </div>
      ))}

      <button type="button" className="evb-passes__dl" onClick={downloadAll}>
        {total === 1 ? 'Download pass' : `Download all ${total} passes`}
      </button>

      {/* Announced rather than only shown: the download itself is silent. */}
      <p className="evb-passes__note" role="status" aria-live="polite" ref={liveRef}>
        {saved || (total > 1 ? 'Each activity has its own pass.' : '')}
      </p>
    </div>
  );
}
